import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, CircleDollarSign, Leaf, Sparkles } from "lucide-react";
import { IMAGES } from "@/config/site";
import HoursWidget from "@/components/HoursWidget";
import Squiggle, { DoodleDot } from "@/components/Squiggle";
import { useSettings } from "@/context/SettingsContext";

export default function Repairs() {
  const { settings } = useSettings();
  const IMG = { ...IMAGES, ...(settings.images || {}) };
  // Show only the mechanical category prominently; show others as sub-mentions
  const mechanical = (settings.services || []).find((s) => s.key === "mechanical");
  const otherServices = (settings.services || []).filter((s) => s.key !== "mechanical");

  return (
    <div data-testid="page-repairs">
      {/* Hero */}
      <section className="relative overflow-hidden px-4 pt-12 pb-10 sm:px-8 md:px-12 md:pt-16">
        <div className="pointer-events-none absolute -top-20 right-0 h-80 w-80 rounded-full bg-leaf-200/60 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
              Repairs · {mechanical?.category || "Mechanical Service"}
            </p>
            <h1 className="mt-3 font-display text-5xl font-black leading-[0.95] tracking-tight text-leaf-900 sm:text-6xl md:text-7xl">
              Honest repairs. <span className="marker-underline italic text-leaf-700">Fast turnaround.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg text-leaf-900/80">
              Drop it off, roll it in, or book a time. We diagnose, quote, and
              get your wheels rolling — no upsells, just what your bike needs.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact#booking"
                data-testid="repairs-cta-book"
                className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
              >
                Request Wrench Time <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#menu"
                data-testid="repairs-cta-menu"
                className="inline-flex items-center gap-2 rounded-full border-2 border-leaf-900 px-6 py-3.5 text-sm font-bold text-leaf-900 transition-all hover:-translate-y-0.5 hover:bg-leaf-900 hover:text-white"
              >
                Jump to menu
              </a>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_20px_60px_rgba(21,128,61,0.18)]">
            <img
              src={IMG.repairs}
              alt="Mechanic working in the Last Stop trailer"
              className="h-[360px] w-full object-cover sm:h-[480px]"
            />
            {/* Sticker badge */}
            <div className="wobble-tilt absolute right-4 top-4 rounded-2xl bg-tomato-500 px-3 py-2 text-xs font-black uppercase tracking-widest text-white shadow-lg">
              Free diagnosis
            </div>
          </div>
        </div>
      </section>

      {/* Squiggle divider */}
      <Squiggle className="px-4 mt-2" />

      {/* How it works */}
      <section className="px-4 py-14 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
            How it works
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-4">
            {[
              { n: "1", t: "Drop off or request", d: "Roll in during shop hours or request wrench time online for a reserved slot." },
              { n: "2", t: "Free diagnosis", d: "We check it over and call with an honest quote before starting." },
              { n: "3", t: "Repair + test", d: "We fix it, tune it, and take it for a quick spin to make sure." },
              { n: "4", t: "You ride happy", d: "Pickup when you're ready. Questions after? We're a call away." },
            ].map((s) => (
              <div
                key={s.n}
                data-testid={`repairs-step-${s.n}`}
                className="relative rounded-3xl border-2 border-leaf-100 bg-white p-6 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-lg"
              >
                <span className="font-display text-5xl font-black text-leaf-200">
                  {s.n}
                </span>
                <h3 className="mt-1 font-display text-xl font-bold text-leaf-900">
                  {s.t}
                </h3>
                <p className="mt-2 text-sm text-leaf-900/70">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mechanical menu */}
      <section id="menu" className="scroll-mt-20 bg-leaf-50 px-4 py-16 sm:px-8 md:px-12">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-leaf-700">
              {mechanical?.category}
            </p>
            <h2 className="mt-2 font-display text-4xl font-bold text-leaf-900 sm:text-5xl">
              Service & pricing
            </h2>
            <p className="mt-3 max-w-2xl text-leaf-900/70">
              Final quote always given after a free hands-on diagnosis. Parts
              and related install additional unless noted.
            </p>
          </div>

          <div className="space-y-4">
            {(mechanical?.items || []).map((item) => (
              <article
                key={item.name}
                data-testid={`repair-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl border-2 border-leaf-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-leaf-300 hover:shadow-lg sm:p-8"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-leaf-600 text-white">
                        <Wrench className="h-4 w-4" />
                      </span>
                      <h3 className="font-display text-2xl font-bold text-leaf-900 sm:text-3xl">
                        {item.name}
                      </h3>
                      <DoodleDot color="tomato" size={8} className="wiggle ml-1" />
                    </div>
                    {item.note && (
                      <p className="mt-2 text-leaf-900/80">{item.note}</p>
                    )}
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sun-100 px-4 py-2 text-lg font-black text-leaf-900">
                    <CircleDollarSign className="h-4 w-4" />
                    {item.price}
                  </span>
                </div>
              </article>
            ))}
          </div>

          {otherServices.length > 0 && (
            <div className="mt-10 rounded-3xl bg-white p-6 sm:p-8">
              <h3 className="font-display text-xl font-bold text-leaf-900">
                Looking for something else?
              </h3>
              <ul className="mt-3 grid gap-2 text-sm text-leaf-900/80 sm:grid-cols-2">
                {otherServices.map((s) => (
                  <li key={s.key} className="flex items-start gap-2">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-leaf-600" />
                    <span>
                      <strong>{s.category}:</strong>{" "}
                      <Link
                        to={s.key === "fitting" ? "/fitting" : "/services"}
                        className="underline hover:text-leaf-700"
                      >
                        See {s.category.toLowerCase()}
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-8 text-center text-sm text-leaf-900/60">
            Don't see what you need?{" "}
            <Link
              to="/contact"
              className="font-bold text-leaf-700 underline hover:text-leaf-900"
            >
              Ask us.
            </Link>{" "}
            We handle specialty work too.
          </p>
        </div>
      </section>

      {/* Eco note + hours */}
      <section className="px-4 py-16 sm:px-8 md:px-12">
        <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[1.2fr_0.8fr]">
          <div
            data-testid="repairs-eco-note"
            className="rounded-[2rem] border-2 border-leaf-200 bg-white p-8 sm:p-10"
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-leaf-100 px-3 py-1 text-xs font-bold uppercase tracking-widest text-leaf-800">
              <Leaf className="h-3.5 w-3.5" /> Repair over replace
            </div>
            <h3 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
              Keep wheels turning. Keep bikes out of landfills.
            </h3>
            <p className="mt-4 text-leaf-900/80">
              Every bike we fix is one less bike in a dumpster. We salvage,
              reuse, and recycle parts whenever we can — and teach you to do the
              same. That's solarpunk service.
            </p>
          </div>
          <HoursWidget />
        </div>
      </section>
    </div>
  );
}
